<script setup lang="ts">
import { useNuxtApi } from '@/composables/useNuxtApi'

import CitiesListComponent from '@/components/Main/CitiesBlock/CitiesList.vue'
import DownloadApp from '@/components/Main/CitiesBlock/DownloadApp.vue'
import SelectCity from '@/components/Main/CitiesBlock/SelectCity.vue'

import videoBackground from '@/assets/video/video.webm'
import type { City, FormattedCity } from '@@/server/types/city'
import ruLayoutConvert from 'convert-layout'
import CyrillicToTranslit from 'cyrillic-to-translit-js'

const DOMAIN_COUNTRY = {
  ru: 'Россия',
  kg: 'Кыргызстан',
  com: 'Казахстан',
}

const { isMobile } = useResponsive()

const searchQuery = ref('')
const isInputCityFocused = ref(false)

const { data: cities, error } = await useNuxtApi<City[]>('/api/cities')

if (error.value) {
  throw new Error(`Ошибка при получении списка городов: ${error.value}`)
}

const citiesList = computed(() => {
  if (!cities.value?.length) {
    return []
  }

  return [...cities.value]?.sort((a: City, b: City) =>
    a.visible_name.localeCompare(b.visible_name, 'ru'),
  )
})

const domainCountry = computed(() => {
  if (import.meta.server) return

  let domain = location?.hostname?.split('.').slice(-1).join('.')

  if (!(domain in DOMAIN_COUNTRY)) {
    domain = 'ru'
  }
  return domain
})

const formattedCitiesList = computed<FormattedCity[]>(() => {
  const formattedCitiesList: FormattedCity[] = []

  for (const city of citiesList.value) {
    // Показываем города текущей страны
    if (
      city?.country?.name ===
      DOMAIN_COUNTRY[domainCountry.value as keyof typeof DOMAIN_COUNTRY]
    ) {
      formattedCitiesList.push({
        ...city,
        cityUrl: `https://${city.domain}`,
      })
    }
  }

  // возвращаем полный список с добавлением страны и первой буквы города
  if (!searchQuery.value) {
    return setCountryAndLetter(formattedCitiesList)
  }
  // возвращаем отфильтрованный список с добавлением первой буквы города
  return setCountryAndLetter(filterList(formattedCitiesList))
})

onMounted(() => {
  document.addEventListener('click', cityInputBlur)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', cityInputBlur)
})

const setCountryAndLetter = (citiesList: City[]) => {
  const parameters: { [key: string]: Array<string> } = {}

  return citiesList.map((city: City) => {
    const firstCountry: string = city.country.name
    const firstLetter: string = city.visible_name[0] || ''

    if (Object.prototype.hasOwnProperty.call(parameters, firstCountry)) {
      if (parameters[firstCountry]?.includes(firstLetter)) {
        return city
      }

      parameters[firstCountry]?.push(firstLetter)
      return { ...city, firstLetter }
    }

    parameters[firstCountry] = [firstLetter]

    return { ...city, firstLetter, firstCountry }
  })
}

const filterList = (citiesList: City[]) => {
  const searchQueryString: string = searchQuery.value
    ? searchQuery.value.toLowerCase().trim()
    : ''

  return citiesList.filter((city: City) => {
    const name: string = city.visible_name.toLowerCase()

    return (
      name.includes(searchQueryString) ||
      // в случае случайной установки раскладки на английский язык
      name.includes(ruLayoutConvert.ru.fromEn(searchQueryString)) ||
      // в случае написания транслитом
      CyrillicToTranslit().transform(name).includes(searchQueryString)
    )
  })
}

const changeFocusCityInput = (isFocused: boolean) => {
  isInputCityFocused.value = isFocused
}

const cityInputBlur = (event: Event) => {
  const searchInput = document.querySelector('#searchInput')
  const citiesList = document.querySelector('#citiesList')
  const buttonOtherCity = document.querySelector('#btnOtherCity')
  const target = event.target as Node

  if (
    searchQuery.value.length ||
    !isInputCityFocused.value ||
    citiesList?.contains(target) ||
    searchInput?.contains(target) ||
    buttonOtherCity?.contains(target)
  ) {
    return
  }

  isInputCityFocused.value = false
}
</script>

<template>
  <div
    class="cities-block"
    :class="{ 'cities-block--active-search': isInputCityFocused }"
  >
    <div v-if="!isMobile" class="cities-block__video">
      <transition name="video">
        <video v-show="!isInputCityFocused" autoplay muted loop>
          <source :src="videoBackground" type="video/mp4" />
        </video>
      </transition>

      <div class="cities-block__video-background"></div>
    </div>

    <div class="cities-block__content">
      <select-city
        v-model="searchQuery"
        :is-input-focused="isInputCityFocused"
        :formatted-cities-list="formattedCitiesList"
        @change-focus="changeFocusCityInput"
      />
      <download-app class="cities-block__download-application" />
    </div>

    <cities-list-component
      :active-search="isInputCityFocused"
      :search-query="searchQuery"
      :formatted-cities-list="formattedCitiesList"
    />
  </div>
</template>

<style scoped lang="scss">
.cities-block {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-height: unset;
  padding: 0 20px 148px 20px;
  margin-bottom: 40px;

  &__video {
    position: absolute;
    left: 0;
    width: 100vw;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .video-enter-active,
    .video-leave-active {
      transition: all 0.3s ease;
    }

    .video-enter-from,
    .video-leave-to {
      overflow: hidden;
      opacity: 0 !important;
    }

    .video-enter-to {
      overflow: hidden;
      opacity: 1 !important;
    }
  }

  &__video-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(25, 26, 30, 0.6);
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 60px;
  }

  &__download-application {
    height: 56px;
    overflow: hidden;
    transition: 0.3s;
  }

  &--active-search {
    justify-content: flex-start;
    padding-bottom: 0;

    .cities-block__content {
      flex: unset;
    }

    .cities-block__download-application {
      height: 0;
      opacity: 0;
    }
  }

  @include media($bp-tablet) {
    min-height: 100vh;
    padding: 0 48px 0 48px;

    &__content {
      padding-top: 182px; // 121px - высота хедера + 61px - отсутп
    }

    &__download-application {
      height: 224px;
    }
  }

  @include media($bp-desktop-mini) {
    padding: 0 0 75px 0;
    margin-bottom: 80px;

    &__video {
      left: calc(((100vw - 1000px) / -2));
    }

    &__content {
      padding-top: 224px; // 90px - хедер + 134px - отступ
    }
  }

  @include media($bp-desktop) {
    margin-bottom: 90px;

    &__video {
      left: calc(
        ((100vw - 1200px) / -2)
      ); // отступ слева учитывая width layout'a
    }

    &__content {
      flex-direction: row;
    }
  }
}
</style>
